import { useEffect } from 'react';
import { initScrollDepthTracking, initTimeOnPageTracking } from '@/lib/analytics';

interface UsePageAnalyticsOptions {
  /** Page path for tracking (defaults to window.location.pathname) */
  pagePath?: string;
  /** Scroll depth thresholds in percentages (default: [25, 50, 75, 100]) */
  scrollThresholds?: number[];
  /** Time on page thresholds in seconds (default: [10, 30, 60, 120, 180]) */
  timeThresholds?: number[];
  /** Enable scroll depth tracking (default: true) */
  trackScrollDepth?: boolean;
  /** Enable time on page tracking (default: true) */
  trackTimeOnPage?: boolean;
}

/**
 * Hook to track page engagement analytics including scroll depth and time on page.
 * Automatically cleans up on unmount.
 * 
 * @example
 * // Basic usage - tracks scroll and time with defaults
 * usePageAnalytics({ pagePath: '/my-page' });
 * 
 * @example
 * // Custom thresholds
 * usePageAnalytics({
 *   pagePath: '/landing',
 *   scrollThresholds: [25, 50, 75, 90, 100],
 *   timeThresholds: [5, 15, 30, 60],
 * });
 * 
 * @example
 * // Track only scroll depth
 * usePageAnalytics({
 *   pagePath: '/article',
 *   trackTimeOnPage: false,
 * });
 */
export function usePageAnalytics({
  pagePath,
  scrollThresholds = [25, 50, 75, 100],
  timeThresholds = [10, 30, 60, 120, 180],
  trackScrollDepth = true,
  trackTimeOnPage = true,
}: UsePageAnalyticsOptions = {}) {
  
  // Track scroll depth
  useEffect(() => {
    if (!trackScrollDepth) return;
    
    const cleanup = initScrollDepthTracking(scrollThresholds, pagePath);
    return cleanup;
  }, [trackScrollDepth, pagePath, scrollThresholds.join(',')]);

  // Track time on page
  useEffect(() => {
    if (!trackTimeOnPage) return;
    
    const cleanup = initTimeOnPageTracking(timeThresholds, pagePath);
    return cleanup;
  }, [trackTimeOnPage, pagePath, timeThresholds.join(',')]);
}

export default usePageAnalytics;
